import { Link } from "react-router-dom";
import { MapPin, Store, HeartPulse, Globe, Mail, Phone } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

/**
 * Carte d'une fiche annuaire (Santé & Perf ou Boutique/Service).
 * @param {{ entry: object, categoryLabel?: string }} props
 */
export function DirectoryEntryCard({ entry, categoryLabel }) {
  if (!entry) return null;
  const isSante = entry.type === "sante";
  const Icon = isSante ? HeartPulse : Store;
  const typeSlug = isSante ? "sante-performance" : "boutiques-services";
  const image = entry.logo_url || entry.photo_url || entry.cover_url || null;
  const initials = (entry.name || "?").split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase();

  return (
    <Link
      to={`/annuaire/${typeSlug}/${entry.slug}`}
      data-testid={`directory-entry-card-${entry.slug}`}
      className="group block"
    >
      <Card className="overflow-hidden aito-card-hover h-full">
        <div className="relative aspect-[4/3] bg-muted flex items-center justify-center overflow-hidden">
          {image ? (
            <img
              src={image}
              alt={entry.name}
              className="w-full h-full object-contain p-4 group-hover:scale-105 transition-transform duration-300"
              loading="lazy"
            />
          ) : (
            <div className="flex flex-col items-center justify-center text-muted-foreground">
              <Icon className="h-10 w-10 mb-2" />
              <span className="text-2xl font-bold">{initials}</span>
            </div>
          )}
          <div className="absolute top-2 left-2">
            <Badge
              variant="secondary"
              className="text-[10px] bg-background/90 backdrop-blur-sm shadow-sm"
              data-testid="directory-entry-badge-type"
            >
              <Icon className="h-3 w-3 mr-1" />
              {isSante ? "Santé" : "Boutique"}
            </Badge>
          </div>
        </div>
        <div className="p-4 space-y-2">
          <h3 className="font-display font-semibold text-base leading-tight line-clamp-2 group-hover:text-[hsl(var(--aito-lagoon))] transition-colors">
            {entry.name}
          </h3>
          {categoryLabel && (
            <p className="text-xs text-muted-foreground line-clamp-1">{categoryLabel}</p>
          )}
          {(entry.commune || entry.island) && (
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <MapPin className="h-3 w-3 shrink-0" />
              <span className="truncate">{[entry.commune, entry.island].filter(Boolean).join(" · ")}</span>
            </div>
          )}
          {(entry.disciplines || []).length > 0 && (
            <div className="flex flex-wrap gap-1 pt-1">
              {entry.disciplines.slice(0, 3).map((d) => (
                <Badge key={d} variant="outline" className="text-[9px] px-1.5 py-0">
                  {d.replace(/_/g, " ")}
                </Badge>
              ))}
            </div>
          )}
          <div className="flex items-center gap-2 pt-2 text-[hsl(var(--aito-lagoon))]">
            {entry.website && <Globe className="h-3 w-3" />}
            {entry.contact_email && <Mail className="h-3 w-3" />}
            {entry.contact_phone && <Phone className="h-3 w-3" />}
          </div>
        </div>
      </Card>
    </Link>
  );
}

export default DirectoryEntryCard;
